import {FullscreenControl, GeolocationControl, Map, Placemark, SearchControl, ZoomControl, useYMaps} from '@pbe/react-yandex-maps';
import React, {useEffect, useRef, useState} from "react";
import {IEvent, IGeoObject} from "yandex-maps";
import {useGlobalContext} from "../../hooks/globalContext";

interface MapModalProps {
    modal: boolean;
    setModalOpen: (open: boolean) => void;
    handleCoordinatesChange: (coords: number[], address: string) => void;
    lat: number | null;
    lon: number | null;
}

const CENTER = [43.240607065190964, 76.91981101091912];
const ZOOM = 13;

export function validMapCoordinates(value: unknown): value is number[] {
    return Array.isArray(value) && value.length === 2 && value.every(Number.isFinite)
        && Math.abs(value[0]) <= 90 && Math.abs(value[1]) <= 180;
}

export function mapAddress(text: unknown, description: unknown, name: unknown): string {
    const clean = (value: unknown) => typeof value === "string" ? value.trim() : "";
    return clean(text) || [clean(description), clean(name)].filter(Boolean).join(", ");
}

export default function MapModal({modal, setModalOpen, handleCoordinatesChange, lat, lon}: MapModalProps) {
    const {i18n} = useGlobalContext();
    const ymaps = useYMaps(["geocode"]);
    const [coordinates, setCoordinates] = useState<number[] | null>(null);
    const [selection, setSelection] = useState<{coordinates: number[]; address: string} | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const request = useRef(0);

    useEffect(() => {
        const savedCoordinates = [lat, lon];
        setCoordinates(validMapCoordinates(savedCoordinates) ? savedCoordinates : null);
        setSelection(null);
        setLoading(false);
        setError(false);
        return () => { request.current += 1; };
    }, [modal, lat, lon]);

    async function selectPlace(lookup: () => PromiseLike<IGeoObject | undefined>, point?: number[]) {
        const id = ++request.current;
        setSelection(null);
        setLoading(true);
        setError(false);
        setCoordinates(point || null);
        let timeout: ReturnType<typeof setTimeout> | undefined;
        try {
            const place = await Promise.race([
                lookup(),
                new Promise<never>((_, reject) => {
                    timeout = setTimeout(() => reject(new Error("Address lookup timed out")), 15000);
                })
            ]);
            if (id !== request.current) return;
            const geometry = place?.geometry;
            const coords = point || (geometry && "getCoordinates" in geometry
                && typeof geometry.getCoordinates === "function" ? geometry.getCoordinates() : null);
            const address = mapAddress(
                place?.properties.get("metaDataProperty.GeocoderMetaData.text", {}),
                place?.properties.get("description", {}),
                place?.properties.get("name", {})
            );
            if (!validMapCoordinates(coords) || !address) throw new Error("Address not found");
            setCoordinates(coords);
            setSelection({coordinates: coords, address});
        } catch {
            if (id === request.current) setError(true);
        } finally {
            clearTimeout(timeout);
            if (id === request.current) setLoading(false);
        }
    }

    function handleClickMap(event: IEvent) {
        const coords = event.get("coords");
        if (!ymaps || !validMapCoordinates(coords)) return;
        void selectPlace(async () => (await ymaps.geocode(coords)).geoObjects.get(0), coords);
    }

    function handleSearchResult(event: IEvent) {
        void selectPlace(() => event.get("target").getResult(event.get("index")));
    }

    function close() {
        request.current += 1;
        setModalOpen(false);
    }

    const savedCoordinates = [lat, lon];
    return (
        <div className={modal ? "form modal effect-scale show" : "modal effect-scale"}
             style={{display: modal ? "block" : "none"}}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content p-4">
                    {modal && <Map defaultState={{
                        center: validMapCoordinates(savedCoordinates) ? savedCoordinates : CENTER,
                        zoom: ZOOM,
                        controls: []
                    }} style={{width: '100%', height: '350px'}} onClick={handleClickMap}>
                        <GeolocationControl options={{float: "left"}}/>
                        <FullscreenControl/>
                        <ZoomControl/>
                        {/* This picker has a Maps key, but no separate Suggest key. */}
                        <SearchControl options={{float: "right", noSuggestPanel: true, noPlacemark: true}}
                                       onResultSelect={handleSearchResult}
                                       onSubmit={() => {
                                           request.current += 1;
                                           setSelection(null);
                                           setLoading(false);
                                           setError(false);
                                       }}
                                       onError={() => {
                                           request.current += 1;
                                           setSelection(null);
                                           setLoading(false);
                                           setError(true);
                                       }}/>
                        {coordinates && <Placemark geometry={coordinates}/>}
                    </Map>}
                    <p className="mt-3 mb-0" role={error ? "alert" : "status"}>
                        {error ? i18n.map_address_error() : loading ? i18n.map_address_loading()
                            : selection?.address || i18n.map_address_hint()}
                    </p>
                    <div className="form__buttons mt-4">
                        <div className="form__buttons-left">
                            <button type="button" className="button -blue modal__button"
                                    disabled={!selection || loading}
                                    onClick={() => {
                                        if (!selection || loading) return;
                                        handleCoordinatesChange(selection.coordinates, selection.address);
                                        close();
                                    }}>
                                {i18n.save()}
                            </button>
                            <button className="button -bordered_blue modal__button" onClick={close}
                                    data-dismiss="modal" type="button">{i18n.cancel()}</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
